import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";
import "../styles/account.css";
import logoutIcon from "../assets/images/logoutIcon.png";

// input icons
import userIcon from "../assets/images/user.png";
import phoneIcon from "../assets/images/phone.png";
import lockIcon from "../assets/images/lock.png";
import eyeIcon from "../assets/images/eye.png";
import eyeOffIcon from "../assets/images/eye-off.png";

const MyAccountSection = ({ currentView, setCurrentView }) => {
  const token = localStorage.getItem("token");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    country: "",
    email: "",
    dob: "",
    phone: "",
    password: "",
    newPassword: "",
    newPasswordConfirm: "",
  });

  const [showNewPw, setShowNewPw] = useState(false);
  const [showNewPw1, setShowNewPw1] = useState(false);
  const [showNewPw2, setShowNewPw2] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/user-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.status && res.data.data) {
          const u = res.data.data;
          setForm((prev) => ({
            ...prev,
            firstName: u.first_name || "",
            lastName: u.last_name || "",
            phone: u.phone || "",
          }));
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      }
    };
    if (token) fetchProfile();
  }, [token]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const canSave = useMemo(() => {
    if (!form.firstName || !form.lastName || !form.phone) return false;
    if (form.newPassword || form.newPasswordConfirm) {
      return (
        form.newPassword.length >= 6 &&
        form.newPassword === form.newPasswordConfirm
      );
    }
    return true;
  }, [form]);

  // ⬇️ UPDATED: calls PUT /user-profile with correct payload
  const handleSave = async (e) => {
    e.preventDefault();

    if (!token) {
      alert("يجب تسجيل الدخول أولاً");
      setCurrentView && setCurrentView("login");
      return;
    }

    // Build payload expected by your ProfileRequest
    const payload = {
      first_name: (form.firstName || "").trim(),
      last_name: (form.lastName || "").trim(),
      phone: (form.phone || "").trim(),
    };

    // Only send password fields if user wants to change the password
    if (form.newPassword || form.newPasswordConfirm) {
      payload.current_password = form.password || "";
      payload.new_password = form.newPassword || "";
      payload.new_password_confirmation = form.newPasswordConfirm || "";
    }

    try {
      setSaving(true);
      const res = await axios.put(`${API_BASE_URL}/user-profile`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (res.data?.status) {
        alert("✅ تم تحديث بيانات الحساب بنجاح.");
        // Clear password fields after successful update
        setForm((f) => ({
          ...f,
          password: "",
          newPassword: "",
          newPasswordConfirm: "",
        }));
      } else {
        // Fallback in case backend didn’t send status:true
        alert("تم الحفظ، لكن لم نتلقَّ تأكيداً واضحاً من الخادم.");
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const { status, data } = err.response || {};
        if (status === 422) {
          const firstMsg =
            (data?.errors && Object.values(data.errors).flat()[0]) ||
            data?.message ||
            "تحقق من الحقول المدخلة.";
          alert(`❌ لم يتم الحفظ:\n${firstMsg}`);
        } else if (status === 401) {
          alert("انتهت الجلسة. يرجى تسجيل الدخول.");
          setCurrentView && setCurrentView("login");
        } else {
          console.error(err);
          alert("حدث خطأ غير متوقع. حاول لاحقًا.");
        }
      } else {
        console.error(err);
        alert("حدث خطأ غير متوقع. حاول لاحقًا.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setCurrentView && setCurrentView("default");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  return (
    <section className="account-section" dir="rtl">
      <div className="clip-bg" />
      <div className="account-wrap">
        <h2 className="account-title">حسابي</h2>

        <form className="account-form" onSubmit={handleSave}>
          {/* Column 1 */}
          <div className="account-col">
            <label className="account-label">الاسم الأول</label>
            <div className="account-input with-icon">
              <img src={userIcon} alt="" className="input-icon" />
              <input
                name="firstName"
                placeholder="أدخل اسمك الأول"
                value={form.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <label className="account-label">كلمة المرور الحالية</label>
            <div className="account-input with-icon">
              <img src={lockIcon} alt="" className="input-icon" />
              <input
                name="password"
                type={showNewPw1 ? "text" : "password"}
                value={form.password}
                onChange={handleChange}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="eye-btn"
                onClick={() => setShowNewPw1((v) => !v)}
              >
                {/* 🔧 use showNewPw1 here (was showNewPw2 by mistake) */}
                <img src={showNewPw1 ? eyeOffIcon : eyeIcon} alt="toggle" />
              </button>
            </div>
          </div>

          {/* Column 2 */}
          <div className="account-col">
            <label className="account-label">الاسم الثاني</label>
            <div className="account-input with-icon">
              <img src={userIcon} alt="" className="input-icon" />
              <input
                name="lastName"
                placeholder="أدخل اسمك الثاني"
                value={form.lastName}
                onChange={handleChange}
                required
              />
            </div>

            <label className="account-label">كلمة المرور الجديدة</label>
            <div className="account-input with-icon">
              <img src={lockIcon} alt="" className="input-icon" />
              <input
                name="newPassword"
                type={showNewPw ? "text" : "password"}
                placeholder="أدخل كلمة المرور الجديدة"
                value={form.newPassword}
                onChange={handleChange}
                autoComplete="new-password"
                minLength={6}
              />
              <button
                type="button"
                className="eye-btn"
                onClick={() => setShowNewPw((v) => !v)}
              >
                <img src={showNewPw ? eyeOffIcon : eyeIcon} alt="toggle" />
              </button>
            </div>
          </div>

          {/* Column 3 */}
          <div className="account-col">
            <label className="account-label">رقم الجوال</label>
            <div className="account-input with-icon">
              <img src={phoneIcon} alt="" className="input-icon" />
              <input
                name="phone"
                placeholder="05xxxxxxxx"
                value={form.phone}
                onChange={handleChange}
                inputMode="tel"
                required
              />
            </div>

            <label className="account-label">تأكيد كلمة المرور الجديدة</label>
            <div className="account-input with-icon">
              <img src={lockIcon} alt="" className="input-icon" />
              <input
                name="newPasswordConfirm"
                type={showNewPw2 ? "text" : "password"}
                placeholder="أعد إدخال كلمة المرور الجديدة"
                value={form.newPasswordConfirm}
                onChange={handleChange}
                autoComplete="new-password"
                minLength={6}
              />
              <button
                type="button"
                className="eye-btn"
                onClick={() => setShowNewPw2((v) => !v)}
              >
                <img src={showNewPw2 ? eyeOffIcon : eyeIcon} alt="toggle" />
              </button>
            </div>
          </div>

          <div className="actions-row">
            <button type="submit" className="save-btn" disabled={!canSave || saving}>
              {saving ? "جارٍ الحفظ..." : "حفظ التغييرات"}
            </button>

            <button type="button" className="logout-btn" onClick={handleLogout}>
              <img src={logoutIcon} alt="" /> تسجيل خروج
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default MyAccountSection;
